/**
 * Проверка контрагента в e-taxes по кнопке у поля ВÖİН (ТЗ-2026-015 §7). Вынесено из
 * processes.tsx по той же причине, что и остальная логика формы: экран в jest не поднимается,
 * а ломается молча именно разбор ответов — «не найден» легко превратить в «ошибка сервера»
 * и напугать пользователя там, где просто нужно заполнить поля руками.
 *
 * Проверка НИЧЕГО не блокирует: не нашли, недоступно, выключено — заявка всё равно подаётся,
 * реквизиты вводятся вручную (вопрос 5 ТЗ).
 */
import { ApiError } from '../../api/client'
import type { Localized } from '../../i18n'
import type { CounterpartyCard } from '../../lib/types'

/** Текст под полем, когда проверка не дала карточки. Всегда с выходом на ручной ввод. */
export function counterpartyLookupError(e: unknown): { ru: string; en: string } {
  const status = e instanceof ApiError ? e.status : 0
  if (status === 404) {
    return {
      ru: 'Контрагент с таким VÖEN в реестре Госналоговой не найден — заполните данные вручную.',
      en: 'No taxpayer with this VÖEN in the state registry — fill in the details manually.',
    }
  }
  if (status === 503) {
    return {
      ru: 'Госпортал сейчас недоступен — заполните данные вручную.',
      en: 'The tax portal is unavailable right now — fill in the details manually.',
    }
  }
  // 400 и прочее ОТ БЭКА: сообщение уже человеческое (например, про 10 цифр) — показываем его.
  // Сетевой сбой сюда не попадает: `fetch` бросает «Failed to fetch», показывать это некому.
  const msg = e instanceof ApiError ? e.message.trim() : ''
  if (msg) return { ru: msg, en: msg }
  return { ru: 'Проверка не удалась — заполните данные вручную.', en: 'Check failed — fill in the details manually.' }
}

export interface CounterpartyBadge {
  label: Localized & { ru: string }
  tone: 'green' | 'red' | 'gray'
}

/**
 * Короткие пометки о найденном контрагенте. Красное — то, на что юротдел обязан посмотреть
 * (§7.2 «рискованный»: подачу не блокирует, решение за юристом), серое — оговорки о свежести.
 */
export function counterpartyBadges(c: CounterpartyCard): CounterpartyBadge[] {
  const badges: CounterpartyBadge[] = [
    {
      tone: 'green',
      label: c.type === 'legalEntity'
        ? { ru: 'Юрлицо', en: 'Legal entity' }
        : { ru: 'Физлицо', en: 'Natural person' },
    },
  ]
  // Риск-статус показываем ВСЕГДА, обоими исходами. Молчание при `risky: false` читается как
  // «риск не проверяли» — а §7.2 требует, чтобы юротдел видел именно результат проверки.
  badges.push(
    c.risky
      ? { tone: 'red', label: { ru: 'Рискованный налогоплательщик', en: 'Risky taxpayer' } }
      : { tone: 'green', label: { ru: 'В списке рискованных не значится', en: 'Not listed as risky' } },
  )
  if (c.active === false) badges.push({ tone: 'red', label: { ru: 'Недействующий', en: 'Inactive' } })
  if (c.vatPayer) badges.push({ tone: 'green', label: { ru: 'Плательщик НДС', en: 'VAT payer' } })
  if (c.stale) {
    badges.push({
      tone: 'gray',
      label: {
        ru: 'Данные сохранённые — с госпорталом сейчас не сверяли',
        en: 'Stored data — not re-checked against the tax portal',
      },
    })
  }
  return badges
}
